import { useEffect, useReducer, useRef, useState, type ReactNode } from 'react';
import { Alert, Button, ButtonGroup, Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Link, Outlet } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  getSampleDataSourcePreference,
  isSampleZipGateRequired,
  setSampleDataSourcePreference,
  subscribeSampleDataSourcePreference,
  type SampleDataSource,
} from '../../services/systems/sample-local/sampleLocalMode';
import {
  consumeSampleZipAutosaveDowngradeNotice,
  disconnectSampleZipSession,
  downloadCurrentSampleZip,
  flushSampleZipSync,
  getSampleZipConnection,
  initSampleZipAutoSync,
  isSampleFsaaSupported,
  isSampleZipConnected,
  pauseSampleZipSession,
  subscribeSampleZipConnection,
  teardownSampleZipAutoSync,
  tryRestoreSampleZipSession,
} from '../../services/systems/sample-local/sampleZipFile';
import { getSampleZipApiError } from '../../services/systems/sample-local/sampleZipErrors';
import { sampleService } from '../../services/sample.service';
import SampleZipGate from './SampleZipGate';
import SampleLocalSwitchPurgeModal from './SampleLocalSwitchPurgeModal';

type SampleZipModeShellProps = {
  /** 指定時はそのまま描画。未指定時は `<Outlet />`（親 Route レイアウト用） */
  children?: ReactNode;
};

function clearSampleQueryCache(qc: ReturnType<typeof useQueryClient>): void {
  // 切替・外部 Zip 再読込時に相手正本／旧スナップショットが一覧に残らないよう除去
  void qc.removeQueries({ queryKey: ['samples'] });
  void qc.removeQueries({ queryKey: ['storageRooms'] });
  void qc.removeQueries({ queryKey: ['storage-rooms'] });
  void qc.removeQueries({ queryKey: ['dashboard'] });
  void qc.removeQueries({ queryKey: ['distribution-analysis'] });
  void qc.removeQueries({ queryKey: ['usage-records-by-date'] });
}

function formatLastSavedAt(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function ShellAlerts({
  error,
  info,
  onClearError,
  onClearInfo,
}: {
  error: string | null;
  info: string | null;
  onClearError: () => void;
  onClearInfo: () => void;
}) {
  if (!error && !info) return null;
  return (
    <Container fluid className="px-3 px-md-4">
      {error ? (
        <Alert variant="danger" dismissible onClose={onClearError} className="mb-2 py-2">
          {error}
        </Alert>
      ) : null}
      {info ? (
        <Alert variant="success" dismissible onClose={onClearInfo} className="mb-2 py-2">
          {info}
        </Alert>
      ) : null}
    </Container>
  );
}

/**
 * サンプル管理の正本シェル（ローカル Zip / クラウド DB は排他・いつでも切替）。
 * クラウド DB はログイン必須。未ログインはローカルのみ。
 */
export default function SampleZipModeShell({ children }: SampleZipModeShellProps) {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, isLoading } = useAuth();
  const qc = useQueryClient();
  const [, bump] = useReducer((n: number) => n + 1, 0);
  const [zipError, setZipError] = useState<string | null>(null);
  const [zipInfo, setZipInfo] = useState<string | null>(null);
  const [switching, setSwitching] = useState(false);
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const fsaa = isSampleFsaaSupported();
  const tRef = useRef(t);
  tRef.current = t;
  const lastEpochRef = useRef(getSampleZipConnection()?.dataEpoch ?? 0);
  const page = children ?? <Outlet />;

  useEffect(() => {
    initSampleZipAutoSync();
    bump();
    const unsubZip = subscribeSampleZipConnection(() => {
      const epoch = getSampleZipConnection()?.dataEpoch ?? 0;
      if (epoch !== lastEpochRef.current) {
        lastEpochRef.current = epoch;
        clearSampleQueryCache(qc);
      }
      bump();
      if (consumeSampleZipAutosaveDowngradeNotice()) {
        setZipInfo(
          tRef.current('sample.zip_autosave_downgraded', {
            defaultValue:
              'Zip への自動書き戻しができなくなりました。変更は「Zip をダウンロード」で保存してください。',
          })
        );
      }
    });
    const unsubPref = subscribeSampleDataSourcePreference(() => bump());
    return () => {
      unsubZip();
      unsubPref();
      void flushSampleZipSync().catch(() => {
        /* unmount */
      });
      teardownSampleZipAutoSync();
    };
  }, [qc]);

  if (isLoading) {
    return (
      <Container className="py-5 text-center text-muted">
        {t('common.loading', { defaultValue: '読み込み中...' })}
      </Container>
    );
  }

  const preference = getSampleDataSourcePreference();
  const zipConnected = isSampleZipConnected();
  const conn = getSampleZipConnection();
  const showGate = isSampleZipGateRequired();

  const finishSwitchToLocal = async (purgeSummary?: {
    deletedSamples: number;
    deletedStorageRooms: number;
  }) => {
    setSampleDataSourcePreference('local');
    clearSampleQueryCache(qc);

    let info: string;
    if (purgeSummary && (purgeSummary.deletedSamples > 0 || purgeSummary.deletedStorageRooms > 0)) {
      info = t('sample.mode_switched_local_after_purge', {
        samples: purgeSummary.deletedSamples,
        rooms: purgeSummary.deletedStorageRooms,
        defaultValue:
          'クラウド上の自分のサンプル {{samples}} 件・保管庫 {{rooms}} 件を永久削除し、ローカル Zip のみに切り替えました。',
      });
    } else if (isSampleZipConnected()) {
      info = t('sample.mode_switched_local', {
        defaultValue: 'ローカル Zip に切り替えました。',
      });
    } else {
      const restored = await tryRestoreSampleZipSession();
      info =
        restored === 'restored'
          ? t('sample.mode_switched_local_restored', {
              defaultValue: 'ローカル Zip に切り替え、前回の作業を再開しました。',
            })
          : t('sample.mode_switched_local_need_zip', {
              defaultValue: 'ローカル Zip に切り替えました。Zip を開くか新規作成してください。',
            });
    }
    setZipInfo(info);
    bump();
  };

  const switchTo = async (next: SampleDataSource) => {
    if (switching || next === preference) return;
    if (next === 'db' && !isAuthenticated) return;
    if (next === 'local' && preference === 'db') {
      setSwitching(true);
      setZipError(null);
      try {
        const preview = await sampleService.previewPurgeOwnedCloudForLocalSwitch();
        const empty =
          preview.data.sampleCount === 0 &&
          preview.data.usageRecordCount === 0 &&
          preview.data.storageRoomCount === 0;
        if (empty) {
          await finishSwitchToLocal();
          return;
        }
        setShowPurgeModal(true);
      } catch (e) {
        setZipError(getSampleZipApiError(e, t));
      } finally {
        setSwitching(false);
      }
      return;
    }
    setSwitching(true);
    setZipError(null);
    try {
      if (next === 'db') {
        if (isSampleZipConnected()) {
          await pauseSampleZipSession();
        }
        setSampleDataSourcePreference('db');
        clearSampleQueryCache(qc);
        setZipInfo(
          t('sample.mode_switched_db', {
            defaultValue: 'クラウド DB に切り替えました（ローカル Zip は停止中）。',
          })
        );
        bump();
      } else {
        await finishSwitchToLocal();
      }
    } catch (e) {
      setZipError(getSampleZipApiError(e, t));
    } finally {
      setSwitching(false);
    }
  };

  // 選択状態はボタンの aria-pressed / variant で十分。説明文は付けない。
  const modeSwitcher = isAuthenticated ? (
    <ButtonGroup size="sm" aria-label={t('sample.mode_switch_aria', { defaultValue: 'データ正本' })}>
      <Button
        variant={preference === 'local' ? 'primary' : 'outline-secondary'}
        disabled={switching}
        aria-pressed={preference === 'local'}
        onClick={() => void switchTo('local')}
      >
        {t('sample.mode_local', { defaultValue: 'ローカル Zip' })}
      </Button>
      <Button
        variant={preference === 'db' ? 'primary' : 'outline-secondary'}
        disabled={switching}
        aria-pressed={preference === 'db'}
        onClick={() => void switchTo('db')}
      >
        {t('sample.mode_db', { defaultValue: 'クラウド DB' })}
      </Button>
    </ButtonGroup>
  ) : (
    <div className="d-flex flex-wrap align-items-center gap-2">
      <span className="small fw-semibold text-primary">
        {t('sample.mode_local', { defaultValue: 'ローカル Zip' })}
      </span>
      <Link to="/login?next=/samples&sampleSource=db" className="small">
        {t('sample.mode_login_for_db', { defaultValue: 'クラウド DB はログイン' })}
      </Link>
    </div>
  );

  const purgeModal = (
    <SampleLocalSwitchPurgeModal
      show={showPurgeModal}
      onCancel={() => setShowPurgeModal(false)}
      onConfirmedAndPurged={(result) => {
        setShowPurgeModal(false);
        setSwitching(true);
        setZipError(null);
        void finishSwitchToLocal({
          deletedSamples: result.deletedSamples,
          deletedStorageRooms: result.deletedStorageRooms,
        })
          .catch((e) => setZipError(getSampleZipApiError(e, t)))
          .finally(() => setSwitching(false));
      }}
    />
  );

  const alerts = (
    <ShellAlerts
      error={zipError}
      info={zipInfo}
      onClearError={() => setZipError(null)}
      onClearInfo={() => setZipInfo(null)}
    />
  );

  const lastSavedLabel =
    conn?.lastSavedAt && !conn.dirty
      ? ` · ${t('sample.zip_saved_at', {
          time: formatLastSavedAt(conn.lastSavedAt, i18n.language),
          defaultValue: '保存 {{time}}',
        })}`
      : '';

  if (showGate) {
    return (
      <>
        <div className="border-bottom bg-light">
          <Container fluid className="px-3 px-md-4 py-2">
            {modeSwitcher}
            {!fsaa ? (
              <p className="small text-muted mb-0 mt-1">
                {t('sample.zip_gate_fsaa_hint', {
                  defaultValue: '保存は「Zip をダウンロード」（Chrome / Edge 推奨）',
                })}
              </p>
            ) : null}
          </Container>
          {alerts}
        </div>
        <Container className="py-3 px-3" id="sample-zip-gate-page">
          <SampleZipGate
            onConnected={() => {
              setSampleDataSourcePreference('local');
              bump();
              setZipError(null);
              clearSampleQueryCache(qc);
            }}
            onError={setZipError}
          />
        </Container>
        {purgeModal}
      </>
    );
  }

  if (preference === 'local' && zipConnected) {
    return (
      <>
        <div className="border-bottom bg-light">
          <Container fluid className="px-3 px-md-4 py-2 d-flex flex-wrap gap-2 align-items-center">
            {modeSwitcher}
            <span className="small text-muted">
              <i className="fas fa-file-archive me-1" aria-hidden="true" />
              {conn?.fileName ? conn.fileName : 'sample-manager.zip'}
              {conn?.autoSaveMode === 'manual-download'
                ? ` · ${t('sample.zip_manual_short', { defaultValue: '手動DL' })}`
                : ''}
              {conn?.dirty
                ? ` · ${t('sample.zip_unsaved', { defaultValue: '未保存の変更あり' })}`
                : lastSavedLabel}
            </span>
            <div className="ms-auto d-flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => {
                  void (async () => {
                    try {
                      await downloadCurrentSampleZip();
                      setZipInfo(
                        t('sample.zip_downloaded', { defaultValue: 'Zip をダウンロードしました' })
                      );
                    } catch (e) {
                      setZipError(getSampleZipApiError(e, t));
                    }
                  })();
                }}
              >
                <i className="fas fa-download me-1" aria-hidden="true" />
                {t('sample.zip_download', { defaultValue: 'Zip をダウンロード' })}
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => {
                  void (async () => {
                    if (
                      conn?.dirty &&
                      !window.confirm(
                        t('sample.zip_disconnect_confirm', {
                          defaultValue:
                            '未保存の変更があります。閉じてゲートに戻りますか？（状態は再開用に保存されます）',
                        })
                      )
                    ) {
                      return;
                    }
                    await disconnectSampleZipSession();
                    bump();
                    clearSampleQueryCache(qc);
                  })();
                }}
              >
                {t('sample.zip_disconnect', { defaultValue: 'Zip を閉じる' })}
              </Button>
            </div>
          </Container>
          {alerts}
        </div>
        {page}
        {purgeModal}
      </>
    );
  }

  // クラウド DB 正本（Zip は未接続）
  return (
    <>
      <div className="border-bottom bg-light">
        <Container fluid className="px-3 px-md-4 py-2 d-flex flex-wrap gap-2 align-items-center">
          {modeSwitcher}
        </Container>
        {alerts}
      </div>
      {page}
      {purgeModal}
    </>
  );
}
